import { useEffect } from 'react';
import { normalizePortalPath } from '../../../../utilities/normalizePortalPath';
import { createFileRoute, useNavigate, useRouterState } from '@tanstack/react-router';
import type { FileRouteTypes } from '@tanstack/react-router';
import useChart from '../../../../hooks/useChart';
import usePatientDoc from '../../../../hooks/usePatientDoc';
import { PatientDoc, Chart } from '@usacsmideo/types';
import { useUpdateUserDoc } from '../../../../hooks/useUpdateUserDoc';
import { useAuth } from '../../../../contexts/AuthContext';
import { classNames } from '../../../../style/classNames';
import { updateNavObjAndGetNextScreen } from '../../../../data/navigation/updateNavObjAndGetNextScreen';
import * as Sentry from '@sentry/browser';
import { Heading } from '../../../../components/Heading';

export const Route = createFileRoute('/_authenticated/$patientId/onboarding/final')({
  component: OnboardingFinal,
});

function OnboardingFinal() {
  const { currentUser } = useAuth();
  const patientId = currentUser?.uid || '';
  const chartQuery = useChart(patientId);
  const chart = chartQuery.data as Chart;
  const patientDocQuery = usePatientDoc(patientId);
  const patientInfo = patientDocQuery.data as PatientDoc;
  const { mutate: mutatePatientDoc } = useUpdateUserDoc();
  const navigate = useNavigate();
  const routerState = useRouterState();
  const currentLocation = routerState.location;
  const pathname = currentLocation.pathname;

  const handleNextButtonClick = async () => {
    const res = await updateNavObjAndGetNextScreen({
      patientId,
      currentPage: pathname,
      patientInfo: patientInfo,
      chart: chart,
    });

    if ('incompleteMessage' in res) {
      // THE PATIENT HAS NOT COMPLETED THE NECESSARY SCREENS
      alert(`Incomplete Information: ${res.incompleteMessage}`);
      return;
    }
    if ('errorMessage' in res) {
      // FIRESTORE ERROR
      alert(`Error: ${res.errorMessage}`);
      return;
    }
    // NAVIGATION COMPLETE — PERSIST FINAL STATE AND NAVIGATE HOME
    if ('complete' in res) {
      mutatePatientDoc(
        { patientId: patientId, navObj: res.updatedNavObj },
        {
          onSuccess: () => {
            navigate({ to: '/' });
          },
          onError: (error: Error) => {
            Sentry.captureException(
              new Error(
                `Error updating patient navigation object for patient ${patientId}: ${error.message}`,
              ),
            );
            console.error('Error updating patient navigation object:', error);
            alert(
              'There was an error updating your information. Please try again later or contact support if this issue persists.',
            );
          },
        },
      );
      return;
    }

    mutatePatientDoc(
      {
        patientId: patientId,
        navObj: res.updatedNavObj,
      },
      {
        onSuccess: () => {
          // NAVIGATE TO THE NEXT SCREEN
          navigate({ to: normalizePortalPath(res.nextScreen.portalPath) as FileRouteTypes['to'] });
        },
        onError: (error: Error) => {
          Sentry.captureException(
            new Error(
              `Error updating patient navigation object for patient ${patientId}: ${error.message}`,
            ),
          );
          console.error('Error updating patient navigation object:', error);
          alert(
            'There was an error updating your information. Please try again later or contact support if this issue persists.',
          );
        },
      },
    );
  };

  const markOnboardingComplete = () => {
    mutatePatientDoc(
      {
        patientId: patientId,
        completedOnboarding: true,
      },
      {
        onSuccess: () => {
          handleNextButtonClick();
        },
        onError: (error: Error) => {
          Sentry.captureException(
            new Error(`Error marking onboarding complete for patient ${patientId}: ${error.message}`),
          );
          console.error('Error marking onboarding complete:', error);
        },
      },
    );
  };

  useEffect(() => {
    if (patientInfo?.navType?.base === 'concierge') {
      const scriptId = 'tegritymarketing-form-embed';
      if (!document.getElementById(scriptId)) {
        const script = document.createElement('script');
        script.id = scriptId;
        script.src = 'https://link.tegritymarketing.com/js/form_embed.js';
        script.type = 'text/javascript';
        document.body.appendChild(script);
      }
    }
  }, [patientInfo?.navType?.base]);

  if (!patientInfo || !patientInfo.navObj || !chart) {
    return <p>Loading...</p>;
  }

  if (patientInfo?.navType?.base === 'self') {
    return (
      <div className="align-center flex min-h-screen flex-col justify-center px-4 py-12 sm:px-6 lg:px-8">
        <div className="mb-8 text-center text-3xl font-bold">Thank you for completing the onboarding process!</div>
        <div className="mb-4 text-center text-lg">You are now ready to begin your self-guided application.</div>
        <button
          id="submit-form-info"
          title="Submit Form Info"
          onClick={markOnboardingComplete}
          disabled={false}
          className={classNames.nextButton}
        >
          Start Application
        </button>
      </div>
    );
  }
  if (patientInfo?.navType?.base === 'concierge') {
    return (
      <div className="w-full">
        <Heading id="online-scheduling-header" textContent="Schedule Appointment Online" />
        <div>
          <p style={{ fontWeight: 'bold', fontSize: 16 }}>
            After scheduling, you may click the button below to continue your application.
          </p>
        </div>
        <iframe
          src="https://schedule.mideohealth.com/widget/booking/7KwGFXZGBAuimz68Q59X"
          style={{ width: '100%', border: 'none', overflow: 'hidden', minHeight: '600px' }}
          scrolling="no"
          id="7KwGFXZGBAuimz68Q59X_1773950457060"
          title="Schedule Appointment"
        />
        <button
          id="submit-form-info"
          title="Submit Form Info"
          onClick={markOnboardingComplete}
          disabled={false}
          className={classNames.nextButton}
        >
          Start Application
        </button>
      </div>
    );
  }
}

export default OnboardingFinal;
